import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useI18n } from "../i18n";
import { useColors, useStyles } from "../ui";
import { ChartBuilder } from "./ChartBuilder";
import { DEFAULT_SPEC } from "../chartSpec";
import type { EChartHandle } from "./EChart";
import {
  encodeDashboard,
  decodeDashboard,
  useSavedViews,
  type SavedView,
} from "../dashboardToken";
import type { ChartSpec } from "../../../shared/src/protocol";

export type DashboardHandle = {
  getCharts: () => Array<{ title: string; png: string | null }>;
};

interface RpcOpts {
  hospitalIds?: string[];
  unitIds?: string[];
  startTime?: number;
  endTime?: number;
  granularity: "day" | "week" | "month";
}

type Panel = "none" | "paste" | "save" | "views";

export const Dashboard = forwardRef<
  DashboardHandle,
  {
    specs: ChartSpec[];
    onSpecsChange: (next: ChartSpec[]) => void;
    filters: RpcOpts;
    refreshKey: number;
  }
>(function Dashboard({ specs, onSpecsChange, filters, refreshKey }, ref) {
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();

  const [panel, setPanel] = useState<Panel>("none");
  const [pasteText, setPasteText] = useState("");
  const [saveName, setSaveName] = useState("");
  const { views, save, remove } = useSavedViews();
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const chartRefs = useRef<Array<{ current: EChartHandle | null }>>([]);
  const getChartRef = (i: number): { current: EChartHandle | null } => {
    if (!chartRefs.current[i]) {
      chartRefs.current[i] = { current: null };
    }
    return chartRefs.current[i];
  };
  useImperativeHandle(ref, () => ({
    getCharts: () =>
      specs.map((spec, i) => ({
        title: chartTitleFromSpec(spec, t),
        png: chartRefs.current[i]?.current?.getPng() ?? null,
      })),
  }));

  const togglePanel = (p: Panel) => {
    setPanel((cur) => (cur === p ? "none" : p));
    setPasteError(null);
    setCopyStatus(null);
  };

  const handleAdd = () => {
    onSpecsChange([...specs, { ...DEFAULT_SPEC }]);
  };

  const handleRemove = (idx: number) => {
    const next = specs.filter((_, i) => i !== idx);
    onSpecsChange(next.length > 0 ? next : [{ ...DEFAULT_SPEC }]);
  };

  const handleDuplicate = (idx: number) => {
    const next = [...specs];
    next.splice(idx + 1, 0, { ...specs[idx] });
    onSpecsChange(next);
  };

  const handleSpecChange = (idx: number, spec: ChartSpec) => {
    const next = [...specs];
    next[idx] = spec;
    onSpecsChange(next);
  };

  const handleCopy = async () => {
    const token = encodeDashboard(specs);
    try {
      await navigator.clipboard.writeText(token);
      setCopyStatus(t("chartBuilder.copied"));
    } catch {
      setCopyStatus(token); // fallback: show the token so user can copy manually
    }
    setTimeout(() => setCopyStatus(null), 4000);
  };

  const handleLoadPaste = () => {
    const decoded = decodeDashboard(pasteText);
    if (!decoded) {
      setPasteError(t("chartBuilder.invalidToken"));
      return;
    }
    onSpecsChange(decoded);
    setPasteText("");
    setPanel("none");
  };

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      await save(name, specs);
      setSaveName("");
      setPanel("views");
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadView = (view: SavedView) => {
    const decoded = decodeDashboard(view.token);
    if (decoded) onSpecsChange(decoded);
    setPanel("none");
  };

  const handleDeleteView = async (name: string) => {
    try {
      await remove(name);
    } catch (e) {
      alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const btn = (active: boolean): React.CSSProperties => ({
    ...styles.btnSmall,
    background: active ? colors.primary : colors.bg,
    color: active ? "#fff" : colors.text,
    borderColor: active ? colors.primary : colors.border,
  });

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: "0.375rem",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "0.5rem",
        }}
      >
        <button onClick={handleAdd} style={btn(false)}>
          + {t("chartBuilder.addChart")}
        </button>
        <button onClick={handleCopy} style={btn(false)}>
          {t("chartBuilder.copyConfig")}
        </button>
        <button onClick={() => togglePanel("paste")} style={btn(panel === "paste")}>
          {t("chartBuilder.pasteConfig")}
        </button>
        <button onClick={() => togglePanel("save")} style={btn(panel === "save")}>
          {t("chartBuilder.saveAs")}
        </button>
        <button
          onClick={() => togglePanel("views")}
          style={btn(panel === "views")}
        >
          {t("chartBuilder.openSaved")} ({views.length})
        </button>
        {copyStatus && (
          <span
            style={{
              fontSize: "0.75rem",
              color: colors.textMuted,
              fontFamily: "ui-monospace, monospace",
              maxWidth: "30rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {copyStatus}
          </span>
        )}
      </div>

      {/* Panels */}
      {panel === "paste" && (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            padding: "0.5rem",
            marginBottom: "0.5rem",
            display: "flex",
            gap: "0.375rem",
            flexDirection: "column",
          }}
        >
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="dlv1:bar:time::cases|line:region::mortalityPct"
            style={{
              ...styles.inputCompact,
              width: "100%",
              fontFamily: "ui-monospace, monospace",
              minHeight: "3rem",
              fontSize: "0.75rem",
            }}
          />
          {pasteError && (
            <span style={{ fontSize: "0.75rem", color: colors.danger }}>
              {pasteError}
            </span>
          )}
          <div style={{ display: "flex", gap: "0.375rem" }}>
            <button onClick={handleLoadPaste} style={btn(true)}>
              {t("chartBuilder.load")}
            </button>
            <button onClick={() => setPanel("none")} style={btn(false)}>
              {t("chartBuilder.cancel")}
            </button>
          </div>
        </div>
      )}

      {panel === "save" && (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            padding: "0.5rem",
            marginBottom: "0.5rem",
            display: "flex",
            gap: "0.375rem",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder={t("chartBuilder.viewName")}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            style={{ ...styles.inputCompact, flex: 1, minWidth: "10rem" }}
          />
          <button
            onClick={handleSave}
            disabled={!saveName.trim() || saving}
            style={btn(true)}
          >
            {saving ? "..." : t("chartBuilder.save")}
          </button>
          <button onClick={() => setPanel("none")} style={btn(false)}>
            {t("chartBuilder.cancel")}
          </button>
        </div>
      )}

      {panel === "views" && (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            padding: "0.5rem",
            marginBottom: "0.5rem",
          }}
        >
          {views.length === 0 ? (
            <span style={{ fontSize: "0.8rem", color: colors.textMuted }}>
              {t("chartBuilder.noSavedViews")}
            </span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {views.map((v) => (
                <div
                  key={v.name}
                  style={{
                    display: "flex",
                    gap: "0.375rem",
                    alignItems: "center",
                    fontSize: "0.8rem",
                  }}
                >
                  <span style={{ flex: 1, color: colors.text, fontWeight: 600 }}>
                    {v.name}
                  </span>
                  <span style={{ fontSize: "0.7rem", color: colors.textMuted }}>
                    {new Date(v.savedAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleLoadView(v)}
                    style={{
                      ...styles.btnSmall,
                      background: colors.primary,
                      color: "#fff",
                      borderColor: colors.primary,
                    }}
                  >
                    {t("chartBuilder.open")}
                  </button>
                  <button
                    onClick={() => handleDeleteView(v.name)}
                    style={{
                      ...styles.btnSmall,
                      background: colors.bg,
                      color: colors.danger,
                      borderColor: colors.border,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chart grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(28rem, 100%), 1fr))",
          gap: "0.5rem",
        }}
      >
        {specs.map((spec, i) => (
          <div
            key={i}
            style={{
              border: `1px solid ${colors.border}`,
              padding: "0.5rem",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <ChartBuilder
              spec={spec}
              onSpecChange={(next) => handleSpecChange(i, next)}
              filters={filters}
              refreshKey={refreshKey}
              onRemove={specs.length > 1 ? () => handleRemove(i) : undefined}
              onDuplicate={() => handleDuplicate(i)}
              chartRef={getChartRef(i)}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

function chartTitleFromSpec(
  spec: ChartSpec,
  t: (key: string) => string,
): string {
  const ct = t(`chartBuilder.type_${spec.chartType}`);
  const dim = t(`chartBuilder.dim_${spec.dimension}`);
  const metric = t(`insights.metric_${spec.metric}`);
  const breakdown = spec.breakdown
    ? ` × ${t(`chartBuilder.dim_${spec.breakdown}`)}`
    : "";
  return `${ct}: ${dim}${breakdown} / ${metric}`;
}
